import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { CoachingApi } from '../../api/coaching.api';
import { CommentApi } from 'app/modules/dashboard/api/comment.api';
import { User } from 'app/models';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { RippleModule } from 'primeng/ripple';
import { AsyncPipe, Location } from '@angular/common';
import {
  BrowseTypesEnum,
  Coaching,
  CoachingPlayerComponent,
} from '@kinomap-dev/ng-ui-lib';
import { CoachingInterval } from '@kinomap-dev/ng-ui-lib/lib/models/coaching-interval.model';
import { GenericViewComponent } from '../../components/generic-view/generic-view.component';
import { VideoApi } from '../../api/video.api';
import { HttpErrorResponse } from '@angular/common/http';
import { FavoriteApi } from '../../api/favorite.api';
import { ToasterService } from 'app/services/toaster.service';

@Component({
  selector: 'app-coaching',
  templateUrl: './coaching.component.html',
  styleUrls: ['./coaching.component.scss'],
  standalone: true,
  imports: [
    RippleModule,
    TranslateModule,
    AsyncPipe,
    GenericViewComponent,
    CoachingPlayerComponent,
  ],
})
export class CoachingComponent {
  editLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  coachingId: number;
  coaching$: Observable<Coaching>;
  intervals$: Observable<CoachingInterval[]>;
  comments: BehaviorSubject<Comment[]> = new BehaviorSubject<Comment[]>([]);
  currentUser: User = null;
  isLiked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  subscriptions: Subscription[] = [];
  sameContributor$: Observable<Coaching[]>;
  intervalsEditMode: boolean = false;
  isMoreMenu: boolean = false;
  isEditScenic: boolean = false;

  private coachingApi: CoachingApi = inject(CoachingApi);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private commentApi: CommentApi = inject(CommentApi);
  public translate: TranslateService = inject(TranslateService);
  private location: Location = inject(Location);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private videoApi: VideoApi = inject(VideoApi);
  private toasterService: ToasterService = inject(ToasterService);

  navPreviousPage(): void {
    this.location.back();
  }

  intervalsEditModeClick(): void {
    this.intervalsEditMode = !this.intervalsEditMode;
    this.isMoreMenu = false;
  }

  videoEditModeClick(): void {
    this.isEditScenic = !this.isEditScenic;
    this.isMoreMenu = false;
  }

  editCoaching(coachingId: number, formData: any): void {
    this.subscriptions.push(
      this.videoApi.postEditCoachingVideo(coachingId, formData).subscribe({
        next: () => {
          this.editLoading$.next(false);
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        },
      })
    );
  }

  onFavoriteClick(coaching: Coaching): void {
    const favoriteAction: Observable<void> = coaching.is_favorite
      ? this.favoriteService.deleteFavorite$(
          coaching.id,
          BrowseTypesEnum.Coaching
        )
      : this.favoriteService.postFavorite$(
          coaching.id,
          BrowseTypesEnum.Coaching
        );
    this.subscriptions.push(
      favoriteAction.subscribe({
        next: () => {
          this.displayToast(coaching.is_favorite);
          // changer le status isFavorite du coaching$
          this.coaching$
            .pipe(
              filter((coaching) => !!coaching),
              switchMap((coaching) => {
                coaching.is_favorite = !coaching.is_favorite;
                return of(coaching);
              })
            )
            .subscribe((updatedCoaching) => {
              this.coaching$ = of(updatedCoaching);
            });
        },
        error: (error) => {
          console.error('Error updating favorite:', error);
        },
      })
    );
  }

  displayToast(isFavorite: boolean) {
    this.toasterService.success(
      null,
      this.translate.instant(
        'coaching.favorites.' + (isFavorite ? 'deleted' : 'added')
      )
    );
  }

  onLikeClick(coaching: Coaching): void {
    const likeAction: Observable<{ data: { likes_count: number } }> =
      coaching.has_liked
        ? this.coachingApi.deleteLike(this.coachingId)
        : this.coachingApi.postLike(this.coachingId);
    this.subscriptions.push(
      likeAction.subscribe(({ data: { likes_count } }) => {
        coaching.likes_count = likes_count;
        this.coaching$ = of(coaching);
      })
    );
  }

  // openSameCoachModal(): void {
  //   const modalClass: string = 'same-coach-modal';
  //   this.modal.display(modalClass);
  //   this.modalRenderedSubscription?.unsubscribe();

  //   setTimeout(() => {
  //     this.modal.setDialogComponent(CoachingSameCoachModalComponent);
  //   });

  //   if (this.modal.dialogComponent instanceof CoachingSameCoachModalComponent) {
  //     return;
  //   }

  //   this.modalRenderedSubscription = this.modal.onModalRendered.subscribe(
  //     (component: CoachingSameCoachModalComponent) => {
  //       component.coach = this.coaching.author;
  //     }
  //   );
  // }

  editVideoDataPost(): void {
    this.isEditScenic = false;
  }

  getComments(): void {
    this.subscriptions.push(
      this.commentApi
        .getPreviewCommentsCoaching(this.coachingId)
        .subscribe((comments: Comment[]) => {
          this.comments.next(comments);
        })
    );
  }

  ngOnInit(): void {
    this.coachingId = this.route.snapshot.params['coachingId'];
    this.coaching$ = this.coachingApi.getCoaching$(this.coachingId);
    this.intervals$ = this.coachingApi.getIntervals$(this.coachingId);
    this.sameContributor$ = this.coaching$.pipe(
      filter((coaching: Coaching) => {
        return !!coaching?.author?.id;
      }),
      switchMap((coaching: Coaching) =>
        this.coachingApi.getCoachingByCoach(coaching.author.id, 4, false)
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
